<div class="faq-window" role="dialog" aria-label="Frequently asked questions"> <!-- NOSONAR -->
  <div class="faq-window__header">
    <h2 class="faq-window__title">Frequently asked questions</h2>
    <button
      type="button"
      class="faq-window__close"
      aria-label="Close"
      data-testid="faq-window-close"
      (click)="onClose()"
    >
      <mat-icon aria-hidden="true">close</mat-icon>
    </button>
  </div>

  <ul class="faq-window__list">
    @for (category of categories; track category.id; let i = $index) {
      <li class="faq-window__item">
        <button
          type="button"
          class="faq-window__item-header"
          [class.faq-window__item-header--expanded]="isExpanded(i)"
          [attr.aria-expanded]="isExpanded(i)"
          [attr.data-testid]="'faq-category-' + category.id"
          (click)="toggle(i)"
        >
          <span class="faq-window__item-title">{{ category.title }}</span>
          <mat-icon
            class="faq-window__chevron"
            [class.faq-window__chevron--expanded]="isExpanded(i)"
            aria-hidden="true"
          >expand_more</mat-icon>
        </button>

        @if (isExpanded(i)) {
          <div class="faq-window__panel" [attr.data-testid]="'faq-panel-' + category.id">
            @for (answer of category.answers; track trackByAnswer($index, answer)) {
              @switch (answer) {
                @case ('copies') {
                  <div class="qa-item" data-testid="qa-copies">
                    <p class="qa-item__question">Will I receive copies after I sign?</p>
                    @if ((displayFundingTransfer$ | async)) {
                      <p class="qa-item__answer">
                        After the closing is complete, a signed copy of the documents will be
                        available. You can also view and download them from your Rocket Account.
                      </p>
                    } @else {
                      <p class="qa-item__answer">
                        After the closing is complete, you can download the documents from Rocket Account.
                      </p>
                    }
                  </div>
                }

                @case ('who') {
                  <div class="qa-item" data-testid="qa-who">
                    <p class="qa-item__question">Who needs to sign the closing documents?</p>
                    <p class="qa-item__answer">
                      Everyone listed on the closing will need to sign the required documents.
                    </p>
                    @if ((borrowers$ | async)?.length) {
                      <ul class="qa-item__list" data-testid="qa-who-signers">
                        @for (user of borrowers$ | async; track trackByPackageUserGuid($index, user)) {
                          <li>{{ user.firstName }} {{ user.lastName }} ({{ roleLabel(user.userRoleCode) }})</li>
                        }
                      </ul>
                    }
                  </div>
                }

                @case ('proof') {
                  <div class="qa-item" data-testid="qa-proof">
                    <p class="qa-item__question">Do I need to show my ID?</p>
                    <p class="qa-item__answer">
                      Yes, you'll need to show a physical form of ID proof, either a driver's license
                      or state&#8209;issued ID to the notary on camera.
                    </p>
                  </div>
                }

                @case ('sessiontake') {
                  <div class="qa-item" data-testid="qa-sessiontake">
                    <p class="qa-item__question">How long does the closing usually take?</p>
                    <p class="qa-item__answer">
                      Most closings take around 35 minutes, though some may be a little shorter
                      or longer.
                    </p>
                  </div>
                }

                @case ('nbthsign') {
                  @if ((hasNbthSigner$ | async)) {
                    <div class="qa-item" data-testid="qa-nbthsign">
                      <p class="qa-item__question">Why does an NBTH need to sign?</p>
                      <p class="qa-item__answer">
                        Even though NBTH signer is not on the loan, they need to sign the
                        documents as they are part of the title.
                      </p>
                    </div>
                  }
                }

                @case ('loaninfo') {
                  @if ((isFaqLoaded$ | async)) {
                    <div class="qa-item qa-item--loan-info" data-testid="qa-loaninfo">
                    <p class="qa-item__question">What are the terms of my loan?</p>
                    @if (disbursementDate$ | async; as disbursementDate) {
                      <p class="qa-item__answer">
                        Disbursement Date: <strong>{{ disbursementDate | date: 'MM/dd/yyyy' : 'UTC' }}</strong>
                      </p>
                    }
                    @if (interestRate$ | async; as interestRate) {
                      <p class="qa-item__answer">
                        Interest Rate: <strong>{{ interestRate | percent: '1.2-4' }}</strong>
                      </p>
                    }
                    @if (monthlyPayment$ | async; as monthlyPayment) {
                      <p class="qa-item__answer">
                        Monthly Payment: <strong>{{ monthlyPayment | currency }}</strong>
                      </p>
                    }
                    @if (firstPaymentDate$ | async; as firstPaymentDate) {
                      <p class="qa-item__answer">
                        First Payment Date: <strong>{{ firstPaymentDate | date: 'MM/dd/yyyy' : 'UTC' }}</strong>
                      </p>
                    }
                    @if (prepaymentPenalty$ | async; as prepaymentPenalty) {
                      <p class="qa-item__answer">
                        Prepayment Penalty: <strong>{{ prepaymentPenalty }}</strong>
                      </p>
                    }
                  </div>
                  }
                }

                @case ('payoffs') {
                  @if ((payoffs$ | async)?.length) {
                    <div class="qa-item" data-testid="qa-payoffs">
                      <p class="qa-item__question">What about payoff &amp; creditor payments?</p>
                      @if (disbursementDate$ | async; as disbursementDate) {
                        <p class="qa-item__answer">
                          Payoffs are completed on your disbursement date:
                          <strong>{{ disbursementDate | date: 'MM/dd/yyyy' : 'UTC' }}</strong>.
                        </p>
                      }
                      <p class="qa-item__answer">
                        Creditor payments are issued as cashier's checks payable to each creditor
                        and sent via UPS (no signature required) on funds disbursement date with
                        instructions included. Any remaining funds are sent directly by Rocket Close.
                      </p>
                      <ul class="qa-item__list qa-item__list--payoffs">
                        @for (payoff of payoffs$ | async; track trackByPayoffRecipient($index, payoff)) {
                          <li>
                            <span class="qa-item__payoff-recipient">{{ payoff.recipient }}</span>
                            <span class="qa-item__payoff-amount">{{ payoff.amount | currency }}</span>
                          </li>
                        }
                      </ul>
                    </div>
                  }
                }

                @case ('escrow') {
                  <div class="qa-item" data-testid="qa-escrow">
                    <p class="qa-item__question">What happens to my escrow account?</p>
                    <p class="qa-item__answer">
                      If Rocket Mortgage is your lender, any escrow overage may be returned to
                      you or applied to your loan principal.
                    </p>
                  </div>
                }

                @case ('autopay') {
                  <div class="qa-item" data-testid="qa-autopay">
                    <p class="qa-item__question">How do I set up autopay?</p>
                    <p class="qa-item__answer">
                      Instructions will be available in your Rocket Mortgage account after funds
                      are disbursed.
                    </p>
                  </div>
                }

                @case ('discrepancies') {
                  <div class="qa-item" data-testid="qa-discrepancies">
                    <p class="qa-item__question">What if my loan terms are incorrect?</p>
                    <p class="qa-item__answer">
                      We are sorry for the inconvenience. Please contact Rocket Mortgage
                      Closing Team:
                      <a class="qa-item__phone" href="tel:8888485309">888-848-5309</a>
                    </p>
                  </div>
                }

                @case ('proceeds') {
                  <div class="qa-item" data-testid="qa-proceeds">
                    <p class="qa-item__question">Do I need to enter bank details?</p>
                    <p class="qa-item__answer">
                      Review the Loan Proceeds Delivery Instructions document and update if needed.
                    </p>
                  </div>
                }

                @case ('incorrect') {
                  <div class="qa-item" data-testid="qa-incorrect">
                    <p class="qa-item__question">What if my address is incorrect?</p>
                    <p class="qa-item__answer">
                      We are sorry for the inconvenience. Please contact Rocket Mortgage
                      Closing Team:
                      <a class="qa-item__phone" href="tel:8888485309">888-848-5309</a>
                    </p>
                  </div>
                }

                @case ('morethaniowe') {
                  @if ((cashToCloseFinalIsFromBorrower$ | async) === false) {
                    <div class="qa-item" data-testid="qa-morethaniowe">
                      <p class="qa-item__question">
                        What if a creditor amount is higher than my balance?
                      </p>
                      <p class="qa-item__answer">
                        Once the creditor receives payment, they are responsible to issue any
                        overage to you. If Rocket Mortgage is the creditor, they will either
                        refund the overage or apply it to your loan principal.
                      </p>
                    </div>
                  }
                }

                @case ('fundsdueatclosing') {
                  @if ((displayFundingTransfer$ | async) === true && (cashToCloseFinalIsFromBorrower$ | async) === true) {
                    <div class="qa-item" data-testid="qa-fundsdueatclosing">
                      <p class="qa-item__question">
                        How do I make a payment for funds due at closing?
                      </p>
                      <p class="qa-item__answer">
                        You will find info on this at the end of the closing.
                      </p>
                    </div>
                  }
                }
              }
            }
          </div>
        }
      </li>
    }
  </ul>
</div>
